import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminBlogAPI } from '../services/api';
import '../styles/BlogView.css';

const BlogView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBlog = React.useCallback(async () => {
    try {
      const response = await adminBlogAPI.getById(id);
      setBlog(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError(err.response?.data?.message || 'Failed to fetch blog');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBlog();
  }, [fetchBlog]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="blog-view-container">
        <div className="loading">Loading blog...</div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="blog-view-container">
        <div className="error-state">
          <p>{error || 'Blog not found'}</p>
          <button className="btn btn-primary" onClick={() => navigate('/blogs')}>
            Back to Blogs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-view-container">
      <div className="blog-view-header">
        <button className="btn btn-secondary" onClick={() => navigate('/blogs')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Blogs
        </button>
      </div>

      <div className="blog-view-content">
        {/* Featured Image */}
        {blog.featuredImage && (
          <div className="blog-featured-image">
            <img
              src={blog.featuredImage}
              alt={blog.title}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Blog Header */}
        <div className="blog-header-section">
          <div className="blog-category-badge">
            {blog.category}
          </div>
          <h1 className="blog-title">{blog.title}</h1>
          <p className="blog-excerpt">{blog.excerpt}</p>

          <div className="blog-meta-info">
            <div className="meta-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <span>{blog.author}</span>
            </div>
            <div className="meta-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span>{blog.publishedAt ? formatDate(blog.publishedAt) : formatDate(blog.createdAt)}</span>
            </div>
            <div className="meta-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>{blog.readTime} min read</span>
            </div>
          </div>

          {blog.status && (
            <div className="blog-status-section">
              <span className={`status-badge-large ${blog.status.toLowerCase()}`}>
                {blog.status}
              </span>
            </div>
          )}
        </div>

        {/* Blog Content */}
        <div className="blog-content-section">
          <div
            className="blog-content-body"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
        </div>

        {/* Tags */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="blog-tags-section">
            <h3>Tags</h3>
            <div className="blog-tags">
              {blog.tags.map((tag, index) => (
                <span key={index} className="blog-tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Share Button */}
        <div className="blog-share-section">
          <button className="btn-share">
            Share
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="18" cy="5" r="3"></circle>
              <circle cx="6" cy="12" r="3"></circle>
              <circle cx="18" cy="19" r="3"></circle>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogView;
